import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@adiox/ui';
import { useStream } from '../stream/stream';
import { useActiveLogin } from '../trading/useTrading';
import { headline, meters, payoutProblem, ruleSentences, type ChallengeDetail, type Tone } from './model';
import { useChallenge } from './useChallenge';

const BAR: Record<Tone, string> = { ok: 'bg-up', warn: 'bg-warn', danger: 'bg-down' };
const TEXT: Record<Tone, string> = { ok: 'text-up', warn: 'text-warn', danger: 'text-down' };
const money = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_WORD: Record<string, string> = {
  Requested: 'Waiting for a decision',
  Approved: 'Approved and paid',
  Rejected: 'Rejected',
};

function Payouts({ detail, login }: { detail: ChallengeDetail; login: number }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canRequest =
    detail.rules.kind === 'Funded' &&
    detail.current.challenge.status === 'Active' &&
    !detail.payouts.some((p) => p.status === 'Requested');

  return (
    <section aria-label="Payouts" className="flex flex-col gap-2" data-testid="payouts">
      <h3 className="text-sm font-semibold">Payouts</h3>
      <p className="text-sm text-muted">
        Available to request now: <strong data-testid="payable">{money(detail.payable)}</strong>
        {detail.paid > 0 ? ` · paid so far ${money(detail.paid)}` : ''}
      </p>
      {canRequest && (
        <form
          className="flex flex-wrap items-end gap-2"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            if (busy) return;
            const message = payoutProblem(amount, detail.payable);
            if (message) return setProblem(message);
            setBusy(true);
            setProblem(null);
            const { response, error } = await api.POST('/api/v1/accounts/{login}/payouts', {
              params: { path: { login } },
              body: { amount: Number(amount) },
            });
            setBusy(false);
            if (!response.ok) {
              const detailText = (error as { detail?: string } | undefined)?.detail;
              return setProblem(detailText ?? 'The request could not be made. Try again.');
            }
            setAmount('');
            setNotice('Payout requested. You will see the decision here.');
            void queryClient.invalidateQueries({ queryKey: ['challenge'] });
          }}
        >
          <div>
            <label htmlFor="payout-amount" className="mb-1 block text-xs uppercase tracking-wide text-muted">
              Amount to request
            </label>
            <input
              id="payout-amount"
              inputMode="decimal"
              className="num w-32 rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg disabled:opacity-40"
          >
            Request payout
          </button>
        </form>
      )}
      <p role="alert" className="min-h-[1.25rem] text-sm text-down">
        {problem}
      </p>
      {notice && (
        <p role="status" className="text-sm" data-testid="payout-notice">
          {notice}
        </p>
      )}
      {detail.payouts.length > 0 && (
        <ul className="divide-y divide-line/40 text-sm" aria-label="Your payout requests">
          {detail.payouts.map((p) => (
            <li key={p.id} className="flex flex-wrap justify-between gap-2 py-1" data-testid="payout-row">
              <span className="num">{money(p.amount)}</span>
              <span>{STATUS_WORD[p.status] ?? p.status}</span>
              {p.note && <span className="text-muted">{p.note}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Where the client stands against the rules of their prop-firm stage, and (when funded) their payouts. */
export function ChallengePanel() {
  const login = useActiveLogin();
  const query = useChallenge();
  const detail = query.data;
  const currency = useStream((s) => (login === undefined ? '' : (s.accounts[login]?.currency ?? '')));

  if (login === undefined) return <p className="p-4 text-sm text-muted">No trading account.</p>;
  if (query.isPending) return <p className="p-4 text-sm text-muted">Loading your challenge…</p>;
  if (query.isError && !detail) {
    return (
      <p role="alert" className="p-4 text-sm text-down">
        The challenge could not be loaded. It will be retried.
      </p>
    );
  }
  if (!detail) {
    return (
      <p className="p-4 text-sm text-muted">
        Account #{login} is not a challenge account, so there are no challenge rules to show.
      </p>
    );
  }

  const funded = detail.rules.kind === 'Funded';
  const head = headline(detail);
  const list = meters(detail.current.progress, funded, currency);
  const c = detail.current.challenge;
  return (
    <section
      aria-label="Challenge"
      className="flex h-full flex-col gap-4 overflow-auto bg-panel p-3"
      data-testid="challenge-panel"
      data-status={c.status}
    >
      <div
        role="status"
        aria-live="polite"
        className={`rounded-md border p-3 ${head.tone === 'danger' ? 'border-down' : head.tone === 'warn' ? 'border-warn' : 'border-line'}`}
        data-testid="challenge-headline"
      >
        <p className={`font-semibold ${TEXT[head.tone]}`}>
          <span aria-hidden="true">{head.mark}</span> {head.title}
        </p>
        <p className="mt-1 text-sm text-muted">{head.detail}</p>
        <p className="mt-1 text-xs text-muted">
          Account #{login} · stage {c.stageIndex + 1} of this attempt · balance{' '}
          {money(detail.current.balance)} · equity {money(detail.current.equity)}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {list.map((m) => (
          <div key={m.id} data-testid={`meter-${m.id}`} data-tone={m.tone}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-semibold">{m.label}</span>
              <span className="text-xs text-muted">{m.text}</span>
            </div>
            <div
              role="meter"
              aria-label={m.label}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(m.percent)}
              aria-valuetext={m.valueText}
              className="mt-1 h-2 overflow-hidden rounded-full bg-panel2"
            >
              <div className={`h-full ${BAR[m.tone]}`} style={{ width: `${m.percent}%` }} />
            </div>
          </div>
        ))}
      </div>

      {(funded || detail.payouts.length > 0) && <Payouts detail={detail} login={login} />}

      {detail.history.length > 1 && (
        <section aria-label="Earlier stages">
          <h3 className="text-sm font-semibold">Your attempt</h3>
          <ul className="mt-1 text-sm">
            {detail.history.map((h) => (
              <li key={h.id} className="flex gap-3" data-testid="history-row">
                <span>Stage {h.stageIndex + 1}</span>
                <span className="text-muted">account #{h.login}</span>
                <span>{h.status}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <details>
        <summary className="cursor-pointer text-sm font-semibold">The rules of this stage</summary>
        <ul className="mt-2 list-disc pl-5 text-sm text-muted">
          {ruleSentences(detail).map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </details>
    </section>
  );
}
